/**
 * AIMessage — Chat message bubble with rich markdown rendering
 *
 * Supports:
 *  - User messages (right-aligned, dark)
 *  - AI messages (left-aligned with avatar, full markdown)
 *  - Streaming indicator (animated dots)
 *  - Error messages (red accent)
 *  - Action cards (confirmation UI)
 *  - Feedback buttons (👍👎) on assistant messages
 */

import React, { memo, useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import MarkdownRenderer from './MarkdownRenderer';
import AIActionCard from './AIActionCard';
import AIResultCard from './AIResultCards';
import AISuggestionChips from './AISuggestionChips';
import AIThinkingTimeline from './AIThinkingTimeline';

/**
 * UserAvatar — Shows user's profile image with broken-image fallback,
 * or first letter of name when no image is available.
 */
function UserAvatar({ profileImage, name }) {
  const [imgError, setImgError] = useState(false);

  const initial = name ? name.charAt(0).toUpperCase() : '';

  const showImage = profileImage && !imgError;

  return (
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm shadow-blue-500/20 ring-2 ring-white dark:ring-gray-900 overflow-hidden">
      {showImage ? (
        <img
          src={profileImage}
          alt="You"
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : initial ? (
        <span className="text-xs font-semibold text-white leading-none select-none">
          {initial}
        </span>
      ) : (
        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
      )}
    </div>
  );
}

/* ── Copy & Retry Buttons ────────────────────────────────────────────────── */

function CopyRetryButtons({ content, onRetry, disabled }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = content;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [content]);

  return (
    <div className="flex items-center gap-0.5">
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className={cn(
          'p-1 rounded-md transition-all duration-150',
          copied
            ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800'
        )}
        title={copied ? 'Copied!' : 'Copy response'}
        aria-label="Copy response"
      >
        {copied ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
          </svg>
        )}
      </button>
      {/* Retry button */}
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={disabled}
          className={cn(
            'p-1 rounded-md transition-all duration-150',
            disabled
              ? 'text-gray-300 dark:text-gray-500 cursor-not-allowed'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800'
          )}
          title="Retry this response"
          aria-label="Retry response"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
        </button>
      )}
    </div>
  );
}
/* ── Feedback Buttons (👍👎) ──────────────────────────────────────────────── */

function FeedbackButtons({ conversationId, messageIndex, onFeedback }) {
  const [rating, setRating] = useState(null); // 'positive' | 'negative' | null

  const handleClick = (value) => {
    // Toggle off if same button pressed again
    const newRating = rating === value ? null : value;
    setRating(newRating);
    if (newRating && onFeedback) {
      onFeedback(conversationId, messageIndex, newRating);
    }
  };

  return (
    <div className="flex items-center gap-0.5 mt-1">
      <button
        onClick={() => handleClick('positive')}
        className={cn(
          'p-1 rounded-md transition-all duration-150',
          rating === 'positive'
            ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
            : 'text-gray-400 hover:text-emerald-500 hover:bg-gray-100 dark:hover:bg-gray-800'
        )}
        title="Good response"
        aria-label="Thumbs up"
      >
        <svg className="w-3.5 h-3.5" fill={rating === 'positive' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
        </svg>
      </button>
      <button
        onClick={() => handleClick('negative')}
        className={cn(
          'p-1 rounded-md transition-all duration-150',
          rating === 'negative'
            ? 'text-red-500 bg-red-50 dark:bg-red-950/30'
            : 'text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800'
        )}
        title="Bad response"
        aria-label="Thumbs down"
      >
        <svg className="w-3.5 h-3.5" fill={rating === 'negative' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715A12.137 12.137 0 0 1 2.25 12c0-2.848.992-5.464 2.649-7.521C5.287 3.997 5.886 3.75 6.504 3.75h4.369a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.5-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.083-.205.173-.405.27-.602.197-.4-.078-.898-.523-.898h-.908c-.889 0-1.713.518-1.972 1.368a12 12 0 0 0-.521 3.507c0 1.553.295 3.036.831 4.398.306.774 1.086 1.227 1.918 1.227h1.053c.472 0 .745-.556.5-.96a8.95 8.95 0 0 1-1.302-4.665c0-1.194.232-2.333.654-3.375Z" />
        </svg>
      </button>
    </div>
  );
}

const AIMessage = memo(function AIMessage({
  message,
  messageIndex,
  conversationId,
  onConfirmAction,
  onCancelAction,
  onFeedback,
  onRetry,
  isLoading,
  followUps,
  onFollowUpClick,
  isStreaming,
  userProfileImage,
  userName,
}) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isError = message.isError;

  return (
    <div
      role="article"
      aria-label={isUser ? 'Your message' : 'AI assistant message'}
      className={cn(
      'flex gap-3 group/msg',
      isUser ? 'justify-end py-2' : 'justify-start py-3',
      'animate-fade-in animate-slide-in-from-bottom-1'
    )}>
      {/* AI Avatar */}
      {isAssistant && (
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center ring-2 ring-white dark:ring-gray-900 shadow-sm">
            <img src="/linaai.png" alt="AI" className="w-9 h-9 rounded-full object-cover" />
          </div>
        </div>
      )}

      {/* Message content */}
      <div className={cn(
        'min-w-0',
        isUser ? 'max-w-[80%] flex flex-col items-end' : 'max-w-[90%] flex flex-col'
      )}>
        {/* Bubble */}
        <div className={cn(
          isUser
            ? 'rounded-2xl rounded-br-md px-4 py-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm shadow-blue-500/15'
            : isError
            ? 'rounded-2xl rounded-bl-md px-4 py-3 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 border border-red-200/80 dark:border-red-800/40'
            : 'px-1 py-0.5'
        )}>
          {/* Content */}
          {isUser ? (
            <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="min-w-0 overflow-hidden ai-response">
              <MarkdownRenderer
                content={message.content}
                className={cn(
                  isError && 'text-red-700 dark:text-red-300'
                )}
              />
            </div>
          )}

          {/* Attached files (user message only) */}
          {isUser && Array.isArray(message.attachments) && message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Attached files">
              {message.attachments.map((att, i) => {
                const name = att.name || att.originalName || 'file';
                const isImage = (att.mimeType || att.type || '').startsWith('image/');
                const sizeKb = typeof att.size === 'number' ? `${(att.size / 1024).toFixed(1)} KB` : null;
                return (
                  <div
                    key={att.fileId || att.id || i}
                    className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-lg bg-white/15 hover:bg-white/20 transition-colors text-white text-[11.5px] leading-tight border border-white/20"
                    title={sizeKb ? `${name} • ${sizeKb}` : name}
                  >
                    {isImage ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                    <span className="truncate max-w-[140px]">{name}</span>
                    {sizeKb && (
                      <span className="opacity-70 text-[10.5px] flex-shrink-0">{sizeKb}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Streaming indicator with step-by-step timeline */}
          {message.isStreaming && (
            <>
              {/* When tokens have started flowing, show a subtle caret —
                  the timeline above already conveys progress. */}
              {message.content ? (
                <div className="flex items-center gap-2 mt-2 h-5">
                  <span className="inline-block w-0.5 h-4 bg-blue-400 dark:bg-blue-500 animate-pulse rounded-full" />
                  <span className="text-[10.5px] text-gray-400 dark:text-gray-500">
                    Writing response…
                  </span>
                </div>
              ) : (
                <AIThinkingTimeline steps={message.steps || []} />
              )}
            </>
          )}
        </div>

        {/* Action card (confirmation UI) */}
        {isAssistant && message.action?.result?.requiresConfirmation && (
          <div className="mt-2.5 w-full">
            <AIActionCard
              action={message.action}
              onConfirm={(pendingId) => onConfirmAction?.(pendingId, message.id)}
              onCancel={(pendingId) => onCancelAction?.(pendingId, message.id)}
              isLoading={isLoading}
            />
          </div>
        )}

        {/* Rich action result card (non-confirmation actions) */}
        {isAssistant && message.action && !message.action.result?.requiresConfirmation && !message.isStreaming && (
          <AIResultCard
            actionName={message.action.name}
            result={message.action.result}
          />
        )}

        {/* Action buttons row (assistant messages only, not streaming, not errors) */}
        {isAssistant && !message.isStreaming && !isError && message.content && (
          <div className="flex items-center gap-0.5 mt-1">
            <CopyRetryButtons
              content={message.content}
              onRetry={onRetry}
              disabled={isLoading || isStreaming}
            />
            <FeedbackButtons
              conversationId={conversationId}
              messageIndex={messageIndex}
              onFeedback={onFeedback}
            />
          </div>
        )}

        {/* Inline follow-up suggestions (below this AI response) */}
        {isAssistant && !message.isStreaming && followUps?.length > 0 && (
          <div className="mt-2 w-full">
            <AISuggestionChips
              suggestions={followUps}
              onSelect={onFollowUpClick}
              disabled={isLoading || isStreaming}
            />
          </div>
        )}
      </div>

      {/* User avatar */}
      {isUser && (
        <div className="flex-shrink-0 mt-0.5">
          <UserAvatar profileImage={userProfileImage} name={userName} />
        </div>
      )}
    </div>
  );
});

export default AIMessage;
